import React from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  useGetPermissionsQuery,
  useGetRoleByIdQuery,
} from "../../infrastructure";
import { textStyles } from "@/lib/textStyles";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface CategoryPermission {
  id: string;
  slug: string;
  generic_name?: string;
  description?: string;
}

interface Category {
  category: string;
  permissions: CategoryPermission[];
}

interface ViewPermissionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  roleId: string | null;
}

const PermissionPill: React.FC<{ label: string; description?: string }> = ({
  label,
  description,
}) => (
  <span className="inline-flex w-fit max-w-full items-center justify-center rounded-lg bg-gray-100 px-2 py-[5px] text-center">
    {description ? (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="break-words cursor-default" style={textStyles.body}>
            {label}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          {description}
        </TooltipContent>
      </Tooltip>
    ) : (
      <span className="break-words" style={textStyles.body}>
        {label}
      </span>
    )}
  </span>
);

export const ViewPermissionsDialog: React.FC<ViewPermissionsDialogProps> = ({
  open,
  onOpenChange,
  roleId,
}) => {
  const { t } = useTranslation("rolesPermissions");
  const { data: allPermissions, isLoading: isLoadingAll } =
    useGetPermissionsQuery();
  const { data: roleData, isLoading: isLoadingRole } = useGetRoleByIdQuery(
    roleId!,
    {
      skip: !roleId,
    },
  );

  const categories = allPermissions as Category[] | undefined;
  const isLoading = roleId ? isLoadingRole : isLoadingAll;

  const role = roleData;
  const rolePermissions = role?.permissions ?? [];

  const rolePermissionIds = React.useMemo(
    () => new Set(rolePermissions.map((perm) => perm.id)),
    [rolePermissions],
  );

  const roleCategories = React.useMemo(() => {
    if (!roleId || rolePermissionIds.size === 0) return [];

    if (categories?.length) {
      return categories
        .map((cat) => ({
          category: cat.category,
          permissions: cat.permissions.filter((perm) =>
            rolePermissionIds.has(perm.id),
          ),
        }))
        .filter((cat) => cat.permissions.length > 0);
    }

    return [];
  }, [roleId, categories, rolePermissionIds]);

  const fallbackRoleGroups = React.useMemo(() => {
    if (!rolePermissions.length) return {};

    const groups: Record<string, { label: string; description?: string }[]> =
      {};

    rolePermissions.forEach((perm) => {
      const slug = ("slug" in perm && perm.slug) || perm.id;
      const displayName =
        ("generic_name" in perm && perm.generic_name) ||
        perm.name ||
        slug;
      const description =
        "description" in perm ? (perm.description as string) : undefined;
      const categoryKey = slug.includes("_")
        ? slug.split("_")[0].charAt(0).toUpperCase() + slug.split("_")[0].slice(1)
        : t("viewPermissionsDialog.other");

      if (!groups[categoryKey]) {
        groups[categoryKey] = [];
      }

      groups[categoryKey].push({ label: displayName, description });
    });

    return groups;
  }, [rolePermissions]);

  const permissionCount = roleId
    ? rolePermissions.length
    : categories?.reduce((count, cat) => count + cat.permissions.length, 0) ?? 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[calc(100dvh-1rem)] flex-col overflow-hidden sm:max-w-[650px]">
        <DialogHeader className="flex flex-col">
          <DialogTitle className="text-xl font-semibold">
            {roleId ? role?.name : t("viewPermissionsDialog.defaultTitle")}
          </DialogTitle>
          <p className="text-xs text-gray-400">
            {roleId
              ? t("viewPermissionsDialog.permissionsCount", { count: permissionCount })
              : t("viewPermissionsDialog.allPermissionsSummary", { count: categories?.length ?? 0 })}
          </p>
        </DialogHeader>

        <div className="mt-4 min-h-0 flex-1 space-y-6 overflow-y-auto">
          {isLoading ? (
            <p className="text-gray-500">{t("viewPermissionsDialog.loading")}</p>
          ) : roleId ? (
            roleCategories.length > 0 ? (
              roleCategories.map((cat) => (
                <div key={cat.category} className="space-y-3">
                  <h3 className="text-sm font-medium text-gray-600">
                    {cat.category}
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {cat.permissions.map((perm) => (
                      <PermissionPill
                        key={perm.id}
                        label={perm.generic_name || perm.slug}
                        description={perm.description}
                      />
                    ))}
                  </div>
                </div>
              ))
            ) : Object.keys(fallbackRoleGroups).length > 0 ? (
              Object.entries(fallbackRoleGroups).map(([category, perms]) => (
                <div key={category} className="space-y-3">
                  <h3 className="text-sm font-medium text-gray-600">
                    {category}
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {perms.map((perm) => (
                      <PermissionPill
                        key={perm.label}
                        label={perm.label}
                        description={perm.description}
                      />
                    ))}
                  </div>
                </div>
              ))
            ) : (
              <p className="text-gray-500">
                {t("viewPermissionsDialog.noneAssigned")}
              </p>
            )
          ) : categories && categories.length > 0 ? (
            categories.map((cat) => (
              <div key={cat.category} className="space-y-3">
                <h3 className="text-sm font-medium text-gray-600">
                  {cat.category}
                </h3>
                {cat.permissions.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {cat.permissions.map((perm) => (
                      <PermissionPill
                        key={perm.id}
                        label={perm.generic_name || perm.slug}
                        description={perm.description}
                      />
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400">{t("viewPermissionsDialog.none")}</p>
                )}
              </div>
            ))
          ) : (
            <p className="text-gray-500">{t("viewPermissionsDialog.noneAvailable")}</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
